import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../context/LoadingContext";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import dayjs from "dayjs";
import {
  fetchData,
  searchfetchQueries,
} from "../../Requirement/services/RequirementServices";
import "bootstrap/dist/css/bootstrap.min.css";

const Requirements = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [activeUser, setActiveUser] = useState("");
  const [requirementList, setRequirementList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    searchProduct: "",
    searchProductGrade: "",
    searchQuantityRange: "",
  });
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);

  const quantityOptions = [
    { value: "", label: "Select Range" },
    { value: "50-100", label: "50-100 MT" },
    { value: "100-200", label: "100-200 MT" },
    { value: "200-500", label: "200-500 MT" },
    { value: "Above-500", label: "Above 500 MT" },
  ];

  const loadAllProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductID",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductID",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setAllProductMaster(allProducts);
      } else {
        setAllProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { Status: "ACTIVE" },
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: { Status: "ACTIVE" },
          }),
        ]);
        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        setProductList(distinctProducts);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const searchRequirements = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);
      try {
        let quantityFilter = {};
        if (searchValue.searchQuantityRange) {
          if (searchValue.searchQuantityRange === "Above-500") {
            quantityFilter = { QuantityGTE: 500 };
          } else {
            const [min, max] = searchValue.searchQuantityRange
              .split("-")
              .map((val) => parseFloat(val));
            quantityFilter = { QuantityGTE: min, QuantityLTE: max };
          }
        }

        let productId = "";
        if (searchValue.searchProduct && searchValue.searchProductGrade) {
          const product = allProductMaster.find(
            (p) =>
              p.ProductName === searchValue.searchProduct &&
              p.Grade === searchValue.searchProductGrade
          );
          if (product) {
            productId = product.ProductID;
          }
        }

        const body = {
          target: "listRequirements",
          userOrder: "CreateDate DESC",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            CategoryType: "requirement",
            ...(searchValue.requirementNumber && {
              RequirementNumber: searchValue.requirementNumber,
            }),
            ProductID: productId,
            ...quantityFilter,
          },
        };
        const response = await fetchData(body);

        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.RequirementsID,
        }));

        setRequirementList(modifiedItems);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading requirements: ", error);
        setRequirementList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading, activeUser, allProductMaster]
  );

  const handleSearch = (searchValues) => {
    setRowStart(0);
    searchRequirements(searchValues, 0);
  };

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchRequirements(searchValues, newRowStart);
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementNumber: value })),
      value: searchValues.requirementNumber,
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProduct: value }));
        loadProductGrades(value);
      },
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
    },
    {
      name: "searchQuantityRange",
      label: "Quantity Range (MT)",
      type: "dropdown",
      options: quantityOptions,
      value: searchValues.searchQuantityRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchQuantityRange: value }));
        searchRequirements({ ...searchValues, searchQuantityRange: value }, 0);
      },
    },
  ];

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Req No." },
    {
      id: "Product",
      numeric: false,
      label: "Product",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.ProductName : "";
      },
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.Grade : "";
      },
    },
    { id: "Quantity", numeric: true, label: "Quantity(MT)" },
    { id: "OriginCountry", numeric: false, label: "Origin" },
    {
      id: "DeliveryLocation",
      numeric: false,
      label: "Destination",
      renderCell: (row) => {
        let res = row.DeliveryLocation?.split("~");
        if (Array.isArray(res)) {
          return res[0];
        }
        return "";
      },
    },
    {
      id: "ShipmentPeriod",
      numeric: false,
      label: "Shipment Period",
      renderCell: (row) => {
        let dpFrom = row.DeliveryPeriodFrom
          ? dayjs(row.DeliveryPeriodFrom).format("DD-MM-YYYY")
          : "";
        let dpTill = row.DeliveryPeriodTill
          ? dayjs(row.DeliveryPeriodTill).format("DD-MM-YYYY")
          : "";
        return dpFrom !== "" && dpTill !== "" ? dpFrom + " to " + dpTill : "";
      },
    },
    {
      id: "DeliveryPort",
      numeric: false,
      label: "Delivery Port",
      renderCell: (row) => {
        let res = row.DeliveryLocation?.split("~");
        if (Array.isArray(res) && res.length > 1) {
          return res[1];
        }
        return "";
      },
    },
    { id: "Incoterm", numeric: false, label: "Incoterms" },
    {
      id: "ProductPackaging",
      numeric: false,
      label: "Packaging",
      renderCell: (row) => {
        if (!row.ProductPackaging) return "";
        const jsonLike = row.ProductPackaging.replace(/{|}/g, "")
          .split(",")
          .map((part) => part.split("="))
          .reduce((obj, [key, value]) => {
            obj[key.trim()] = value ? value.trim() : "";
            return obj;
          }, {});
        return jsonLike.item || "";
      },
    },
    {
      id: "RequirementDate",
      numeric: false,
      label: "Created",
      renderCell: (row) =>
        row.RequirementDate
          ? dayjs(row.RequirementDate).format("DD-MM-YYYY")
          : "",
    },
    {
      id: "ValidUntil",
      numeric: false,
      label: "Expires",
      renderCell: (row) =>
        row.ValidUntil ? dayjs(row.ValidUntil).format("DD-MM-YYYY") : "",
    },
    {
      id: "Buyer",
      numeric: false,
      label: "Buyer",
      renderCell: (row) => {
        return "Verified";
      },
    },
  ];

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        } else {
          setActiveUser(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };
    checkUser();
    loadProductList();
    loadAllProducts();
  }, [navigate, loadProductList, loadAllProducts]);

  useEffect(() => {
    if (activeUser && allProductMaster.length > 0) {
      searchRequirements(searchValues);
    }
  }, [
    rowStart,
    pageSize,
    activeUser,
    searchRequirements,
    searchValues,
    allProductMaster,
  ]);

  return (
    <div>
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Requirements</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={requirementList}
        total={totalItems}
        title="Requirement Information"
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        currentPage={currentPage}
      />
    </div>
  );
};

export default Requirements;
